import { UserRole } from "@/apis/bff"
import { RoleDataUser } from "@/lib/fetch-users"
import { WaitingRoomAppointmentsInput } from "@/modules/waiting-room/services/__generated__/useSlotUpdatedSubscription.graphql"
import { AppointmentStatus, Language } from "@/types/__generated__/union-types"

export const AVAILABLE_TO_ME_WORKING_ROLES = ["MedicalSupervisor", "Doctor"] as const

export type AvailableToMeWorkingRole = (typeof AVAILABLE_TO_ME_WORKING_ROLES)[number]

export const checkStatusesIsCancelledOrClosed = (statuses: ReadonlyArray<AppointmentStatus>): boolean => {
  const targetStatuses: AppointmentStatus[] = ["CANCELLED", "CLOSED"]

  return statuses.some((status) => targetStatuses.includes(status))
}

export const getDoctorLanguagesSkill = (roleDataUser: RoleDataUser): Language[] => {
  const languages: Language[] = []
  if (roleDataUser.roleData?.languages) {
    for (const value of roleDataUser.roleData.languages) {
      if (value.skill) {
        languages.push(value.skill)
      }
    }
  }
  return languages
}

export const getAvailableToMeFilters = (
  role: UserRole,
  userName: string,
  doctorLanguages: Language[] | null,
): WaitingRoomAppointmentsInput => {
  switch (role) {
    case "Doctor":
      return {
        agents: { hasUnassigned: true, userNames: [userName] },
        languages: doctorLanguages,
        channels: ["CHAT", "PHONE", "VIDEO"],
        services: [
          "EMERGENCY_LINE",
          "CONSULTATION",
          "KIDS_LINE",
          "PREVENTION_ASSESSMENT",
          "TELE_GENERAL_PRACTITIONER",
          "MEDGATE_CONNECT",
          "PSYCHOSOCIAL",
        ],
        dateRange: null,
        statuses: ["OPEN", "IN_PROGRESS"],
        reviewNeeded: null,
        specialties: null,
      }
    case "NursingProfessional":
      return {
        agents: { hasUnassigned: true, userNames: [userName] },
        languages: null,
        channels: ["CHAT", "PHONE", "VIDEO"],
        services: ["KIDS_LINE"],
        dateRange: null,
        statuses: ["OPEN", "IN_PROGRESS"],
        specialties: null,
      }
    case "CallCenterAgent":
    case "CustomerServiceCenter":
      return {
        agents: { hasUnassigned: true, userNames: [userName] },
        languages: null,
        channels: ["CHAT", "PHONE", "VIDEO"],
        services: ["ADMINISTRATIVE_REQUEST"],
        dateRange: null,
        statuses: ["OPEN", "IN_PROGRESS"],
        specialties: null,
      }
    case "MedicalSupervisor":
      return {
        agents: null,
        languages: null,
        channels: ["CHAT", "PHONE", "VIDEO"],
        services: null,
        dateRange: null,
        statuses: ["OPEN", "IN_PROGRESS"],
        reviewNeeded: null,
        specialties: null,
      }
  }
}

export const getDefaultAvailableToMeWorkingRole = (role: UserRole): AvailableToMeWorkingRole | null =>
  role === "MedicalSupervisor" ? "Doctor" : null

export const getDefaultWaitingRoomFilters = (
  role: UserRole,
  userName: string,
  doctorLanguages: Language[] | null,
): WaitingRoomAppointmentsInput =>
  getAvailableToMeFilters(getDefaultAvailableToMeWorkingRole(role) ?? role, userName, doctorLanguages)
